import React, { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { toast } from "react-toastify";
import { Spinner } from "react-bootstrap";
import { api } from "../../api/apiService";
import moment from "moment";

function PrintPayslips() {
    const [searchParams] = useSearchParams();
    const id = searchParams.get("id");
    const departmentId = searchParams.get("departmentId");
    const [payrollData, setPayrollData] = useState(null);
    const [isLoading, setIsLoading] = useState(true);

    const company = localStorage.getItem("company");
    const payDate = moment().format("DD-MM-YY");

    useEffect(() => {
        const fetchData = async () => {
            try {
                setIsLoading(true);
                const response = await api.get(`/payrolls/${id}/details?department_id=${departmentId}`);
                setPayrollData(response.data);
                setIsLoading(false);
                setTimeout(() => {
                    window.print();
                    window.addEventListener("afterprint", handleAfterPrint);
                }, 1000);
            } catch (error) {
                setIsLoading(false);
                toast.error("Failed to fetch job data");
            }
        };

        fetchData();
        return () => {
            window.removeEventListener("afterprint", handleAfterPrint);
        };
    }, []);

    const handleAfterPrint = () => {
        window.close();
    };

    const formatNumber = (value) => {
        const roundedValue = Math.round(value * 10) / 10;
        return new Intl.NumberFormat('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(roundedValue);
    };

    return (
        <>
            <style>
                {`
    @media print {
        .payroll-container {
            display: flex;
            flex-wrap: wrap;
        }

        .payroll-group {
            width: 100%;
            page-break-inside: avoid;
        }

        /* Ensures no blank page is added */
        .payroll-group:last-of-type {
            page-break-after: auto;
        }
    }
    `}
            </style>
            <main className="main m-5">
                {isLoading ? (
                    <div className="d-flex align-items-center justify-content-center vh-60">
                        <Spinner animation="border" className="text-primary" />
                    </div>
                ) : (
                    <>
                        {payrollData?.payroll_entries?.length > 0 ? (
                            payrollData?.payroll_entries?.reduce((acc, payroll, index) => {
                                const createdAt = moment(payroll.created_at);
                                const payPeriodStart = createdAt.startOf('month').format("DD-MM-YY");
                                const payPeriodEnd = createdAt.endOf('month').format("DD-MM-YY");
                                const baseSalary = Number(payroll.calc_days) * Number(payroll.pay_rate);
                                const grossSalary = baseSalary + Number(payroll.bonus);

                                // Group every 3 payroll slips together
                                if (index % 2 === 0) acc.push([]);

                                acc[acc.length - 1].push(
                                    <section key={index} className="section pb-5 my-5 border-bottom">
                                        <div className="row justify-content-center text-center w-100 text-dark">
                                            <div className="col-sm-12">
                                                <h4 className="fw-bold">Salary Slip</h4>
                                                <div className="my-3">
                                                    <span className="fw-bold">Pay Period: </span>
                                                    {payPeriodStart} to {payPeriodEnd}
                                                    <span className="mx-2 fw-bold">|</span>
                                                    <span className="fw-bold">Pay Date: </span> {payDate || "---"}
                                                </div>
                                                <div className="row d-flex flex-column align-items-center">
                                                    <div className="text-start col-sm-12 col-md-8 w-sm-95">
                                                        <div>Employee Name: <span className="fw-bold">{payroll.employee?.first_name} {payroll.employee?.last_name}</span></div>
                                                        <div>Employee ID: <span>{payroll.employee?.employee_id}</span></div>
                                                        <div>Designation: <span>{payroll.employee?.role?.role_name}</span></div>
                                                    </div>
                                                    <div className="col-sm-12 col-md-8 w-sm-95 my-3">
                                                        <div className="table-responsive">
                                                            <table className="table border border-dark border-top-dark text-start">
                                                                <thead>
                                                                    <tr>
                                                                        <th className="border border-dark bg-transparent"><strong>Earnings</strong></th>
                                                                        <th className="border border-dark bg-transparent text-end"><strong>Amount</strong></th>
                                                                    </tr>
                                                                </thead>
                                                                <tbody>
                                                                    <tr>
                                                                        <td className="border border-dark bg-transparent">Base Salary</td>
                                                                        <td className="border border-dark bg-transparent text-end">{formatNumber(baseSalary)}</td>
                                                                    </tr>
                                                                    <tr>
                                                                        <td className="border border-dark bg-transparent">Bonus</td>
                                                                        <td className="border border-dark bg-transparent text-end">{formatNumber(payroll.bonus)}</td>
                                                                    </tr>
                                                                    <tr>
                                                                        <td className="border border-dark bg-transparent"><strong>Gross Salary</strong></td>
                                                                        <td className="border border-dark bg-transparent text-end"><strong>{formatNumber(grossSalary)}</strong></td>
                                                                    </tr>
                                                                </tbody>
                                                            </table>
                                                        </div>
                                                    </div>
                                                    <div className="col-sm-12 col-md-8 mt-4 d-flex justify-content-between">
                                                        <div className="d-flex flex-column align-items-center">
                                                            <p className="mb-5">Employer Signature</p>
                                                            <div className="w-100 border-dark border-bottom"></div>
                                                        </div>
                                                        <div className="d-flex flex-column align-items-center">
                                                            <p className="text-center mb-5">Employee Signature</p>
                                                            <div className="w-100 border-dark border-bottom"></div>
                                                        </div>
                                                    </div>
                                                    <div className="col-sm-12 col-md-8 text-start pt-2 mt-5">
                                                        <div className="text-start">
                                                            <p className="fw-bold">{company}</p>
                                                        </div>
                                                    </div>
                                                </div>
                                            </div>
                                        </div>
                                    </section>
                                );

                                return acc;
                            }, []).map((group, i) => (
                                <div key={i} className="payroll-group">
                                    {group}
                                </div>
                            ))
                        ) : (
                            <p className="d-flex align-items-center justify-content-center vh-60 text-muted">
                                No payroll data available
                            </p>
                        )}
                    </>
                )}
            </main>

        </>
    )
}

export default PrintPayslips